import { useState, useEffect } from 'react';
import { Gift, X, Loader2, Package, Tag } from 'lucide-react';
import { api } from '@/api/apiClient';
import { toast } from 'sonner';

/**
 * PromoPicker — lets the rep MANUALLY apply a manager-defined promotion to the
 * current cart. It fetches active promos from the backend, and on selection
 * asks the backend to apply the promo to the cart lines. The backend validates
 * conditions (enough qty / bundle components present) and returns the full new
 * line set, which the parent swaps into its cart via `onApplied`.
 *
 * Fully additive: a self-contained modal, no changes to existing components.
 */
export default function PromoPicker({ open, onClose, partnerId, orderLines, onApplied }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get('/sellgo_promos')
      .then(res => { if (alive) setPromos((res?.data) || []); })
      .catch(() => { if (alive) setPromos([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const apply = async (promo) => {
    setApplyingId(promo.id);
    try {
      const res = await api.post('/sellgo_promo_apply', {
        promo_id: promo.id,
        partner_id: partnerId,
        lines: orderLines,
      });
      if (res?.success && res?.data?.lines) {
        onApplied(res.data.lines, res.data.bundle_summary || null);
        toast.success(res.message || 'تم تطبيق العرض');
        onClose();
      } else {
        toast.error(res?.error || 'تعذّر تطبيق العرض');
      }
    } catch (e) {
      // apiClient throws ApiError on any non-2xx; surface the backend's real
      // message (e.g. "الكمية غير كافية") instead of a generic one.
      toast.error(e?.data?.error || e?.message || 'تعذّر تطبيق العرض');
    } finally {
      setApplyingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-xl" dir="rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <Gift className="w-4 h-4 text-primary" /> العروض المتاحة
          </h3>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {!loading && promos.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-10">لا توجد عروض متاحة حالياً</p>
          )}

          {!loading && promos.map(p => (
            <button
              key={p.id}
              onClick={() => apply(p)}
              disabled={applyingId !== null}
              className="w-full text-right bg-muted/40 hover:bg-muted border border-border rounded-xl p-3 flex items-center gap-3 transition-colors disabled:opacity-60"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                {p.promo_type === 'qty_gift'
                  ? <Gift className="w-4 h-4 text-primary" />
                  : <Tag className="w-4 h-4 text-primary" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground line-clamp-1">{p.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{p.desc}</p>
              </div>
              {applyingId === p.id && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </button>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-border">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Package className="w-3 h-3" /> يطبّق العرض على المنتجات الموجودة في السلة الآن.
          </p>
        </div>
      </div>
    </div>
  );
}
